<template>
  <div>
    <!-- Hero MMM -->
    <section class="mmm-hero">
      <div class="mmm-hero-container">
        <div class="mmm-hero-grid">
          <div class="mmm-hero-copy">
            <p class="mmm-hero-brand">Mes Meilleurs Menus</p>
            <h1>Des menus équilibrés et personnalisés pour mieux manger au quotidien</h1>
            <p class="mmm-hero-lead">Se faire plaisir sans se compliquer la vie, avec une alimentation bonne pour sa santé et plus respectueuse du climat. Mes Meilleurs Menus m’accompagne gratuitement, semaine après semaine.</p>
            <router-link to="/inscription" class="mmm-hero-button">
              Je m'inscris gratuitement
              <font-awesome-icon icon="fa-solid fa-right-to-bracket" />
            </router-link>
          </div>
          <div class="mmm-hero-visual">
            <img
              :src="require('@/assets/accueil/menus-equilibres-personnalises-famille-mmm.png')"
              alt="Famille préparant des menus équilibrés et personnalisés avec Mes Meilleurs Menus"
              class="mmm-hero-image"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="mmm-features-section">
      <b-container>
        <div class="mmm-features-heading">
          <p class="mmm-features-kicker">MES MEILLEURS MENUS, CONCRÈTEMENT</p>
          <h2>Une semaine de menus pensée pour moi et ma famille</h2>
        </div>

        <b-row class="justify-content-center">
          <b-col cols="12" md="6" lg="4" class="mb-4">
            <article class="mmm-feature-card">
              <img class="mmm-feature-illustration" src="../assets/accueil/menus-personnalises-profils-famille.png" alt="Menus et portions adaptés au profil et aux besoins de chaque personne" loading="lazy">
              <h3>Mes portions sont adaptées à mes besoins</h3>
              <p>À partir de mon profil, l’algorithme scientifique adapte mes portions à mes besoins en énergie, protéines, glucides, lipides et fibres.</p>
            </article>
          </b-col>

          <b-col cols="12" md="6" lg="4" class="mb-4">
            <article class="mmm-feature-card">
              <img class="mmm-feature-illustration" src="../assets/accueil/preferences-alimentaires-menus.png" alt="Préférences alimentaires pour personnaliser ses menus" loading="lazy">
              <h3>Je choisis comment je veux manger</h3>
              <p>Je peux choisir une alimentation classique, flexitarienne ou végétarienne, mais aussi privilégier des menus sans gluten ou à faible profil glycémique.</p>
            </article>
          </b-col>

          <b-col cols="12" md="6" lg="4" class="mb-4">
            <article class="mmm-feature-card">
              <img class="mmm-feature-illustration" src="../assets/accueil/equilibre-nutritionnel-semaine.png" alt="Équilibre nutritionnel des menus vérifié sur toute la semaine" loading="lazy">
              <h3>Mon équilibre est vérifié sur toute la semaine</h3>
              <p>Mes Meilleurs Menus vérifie que mes menus couvrent mes besoins en <strong>24 nutriments</strong> — vitamines, minéraux et fibres notamment — tout en favorisant une alimentation variée.</p>
            </article>
          </b-col>

          <b-col cols="12" md="6" lg="4" class="mb-4">
            <article class="mmm-feature-card">
              <img class="mmm-feature-illustration" src="../assets/accueil/menus-bas-carbone.png" alt="Menus bas-carbone avec davantage d’aliments végétaux et des fruits et légumes de saison" loading="lazy">
              <h3>Je privilégie les saisons et je pense au climat</h3>
              <p>Mes menus font davantage de place aux aliments végétaux, limitent la viande et privilégient les <strong>fruits et légumes de saison</strong>, afin de contribuer à réduire l’impact climatique de mon alimentation.</p>
            </article>
          </b-col>
          <b-col cols="12" md="6" lg="4" class="mb-4">
            <article class="mmm-feature-card">
              <img class="mmm-feature-illustration" src="../assets/accueil/mes-meilleurs-menus-gratuit.png" alt="Mes Meilleurs Menus est entièrement gratuit" loading="lazy">
              <h3>C’est gratuit</h3>
              <p>Mes Meilleurs Menus est géré par une association et vise à aider gratuitement le plus grand nombre à améliorer ses habitudes alimentaires.</p>
            </article>
          </b-col>

        </b-row>
      </b-container>
    </section>
    <section class="mmm-how-section">
      <b-container>
        <div class="mmm-how-heading">
          <p class="mmm-how-kicker">SIMPLE ET FLEXIBLE</p>
          <h2>Comment ça marche ?</h2>
          <p>Mes Meilleurs Menus prépare ma semaine, mais je garde toujours la main pour l'adapter à ma vraie vie.</p>
        </div>

        <div class="mmm-how-grid">
          <article class="mmm-how-step">
            <div class="mmm-how-top"><span class="mmm-how-number">1</span><img class="mmm-how-illustration" src="../assets/accueil/personnalisation-profil-menus.png" alt="Personnalisation des menus selon le profil et les préférences" loading="lazy"></div>
            <h3>Je renseigne mon foyer</h3>
            <p>Je renseigne mon foyer, les profils de chacun (âge, taille, poids…), mon activité physique, mes préférences alimentaires, mon temps pour cuisiner, mes goûts et allergies, ainsi que certaines préférences comme le budget.</p>
          </article>
          <article class="mmm-how-step">
            <div class="mmm-how-top"><span class="mmm-how-number">2</span><img class="mmm-how-illustration" src="../assets/accueil/algorithme-menus-semaine.png" alt="Algorithme nutritionnel construisant une semaine de menus personnalisés" loading="lazy"></div>
            <h3>L’algorithme construit ma semaine de menus</h3>
            <p>L’algorithme construit ma semaine de repas en s’appuyant sur les connaissances scientifiques et les recommandations nutritionnelles. Il recherche le meilleur équilibre entre mes besoins nutritionnels, mes choix alimentaires, la saisonnalité et mes contraintes du quotidien. Je retrouve mes menus et mes recettes en ligne et je reçois également mon programme de la semaine en PDF par e-mail.</p>
          </article>
          <article class="mmm-how-step">
            <div class="mmm-how-top"><span class="mmm-how-number">3</span><img class="mmm-how-illustration" src="../assets/accueil/adapter-repas-personnalise.png" alt="Possibilité de remplacer et adapter ses repas" loading="lazy"></div>
            <h3>Je peux modifier mes repas</h3>
            <p>Un plat ne me convient pas ? Je peux le remplacer par une autre proposition, l'un de mes plats préférés ou un repas utilisant un ingrédient que j'ai déjà chez moi.</p>
          </article>
          <article class="mmm-how-step">
            <div class="mmm-how-top"><span class="mmm-how-number">4</span><img class="mmm-how-illustration" src="../assets/accueil/organisation-repas-semaine.png" alt="Adaptation des repas à l'organisation de la semaine" loading="lazy"></div>
            <h3>J’organise mes repas selon mon quotidien</h3>
            <p>Restaurant, déjeuner à l'extérieur, changement de programme ? J'indique les repas que je ne prendrai pas à la maison. Et si je cuisine suffisamment pour deux repas, les restes peuvent être prévus pour le lendemain.</p>
          </article>
          <article class="mmm-how-step">
            <div class="mmm-how-top"><span class="mmm-how-number">5</span><img class="mmm-how-illustration" src="../assets/accueil/portions-adaptees-foyer.png" alt="Portions adaptées aux différents membres du foyer" loading="lazy"></div>
            <h3>Les quantités sont calculées pour mon foyer</h3>
            <p>Mes Meilleurs Menus calcule les quantités nécessaires en fonction des personnes prévues à chaque repas, avec des portions adaptées aux différents profils de mon foyer.</p>
          </article>
          <article class="mmm-how-step">
            <div class="mmm-how-top"><span class="mmm-how-number">6</span><img class="mmm-how-illustration" src="../assets/accueil/liste-courses-personnalisee.png" alt="Liste de courses adaptée automatiquement aux repas" loading="lazy"></div>
            <h3>Ma liste de courses est générée pour ma semaine de repas</h3>
            <p>Les ingrédients et les quantités correspondent aux repas réellement prévus. Je peux retirer ce que j'ai déjà chez moi et obtenir une liste de courses avec ce qu'il me reste vraiment à acheter.</p>
          </article>
        </div>

        <div class="mmm-how-action">
          <router-link to="/mmm-en-pratique" class="mmm-how-btn">Découvrir toutes les possibilités de personnalisation <span aria-hidden="true">→</span></router-link>
        </div>
      </b-container>
    </section>

    <section class="mmm-recipe-search-section">
      <b-container>
        <div class="mmm-recipe-search-card">
          <div class="mmm-recipe-search-copy">
            <p class="mmm-recipe-search-kicker">ENVIE DE CUISINER TOUT DE SUITE ?</p>
            <h2>Je cherche simplement une recette de saison ?</h2>
            <p>Pas besoin de préparer toute une semaine de menus. Je peux aussi rechercher directement une recette et la filtrer selon mes envies : <strong>de saison</strong>, <strong>végétarienne</strong>, avec viande ou poisson, <strong>sans gluten</strong>, à <strong>faible index glycémique</strong>, <strong>bon marché</strong> ou selon mon temps disponible.</p>
            <a class="mmm-recipe-search-btn" href="/recettes?season=actuelle">Trouver une recette de saison <span aria-hidden="true">→</span></a>
          </div>
        </div>
      </b-container>
    </section>

    <section class="mmm-mealim-invite">
      <div class="mmm-mealim-invite-visual" role="img" aria-label="Illustration scientifique du score MEALIM et de ses critères nutritionnels">
        <svg viewBox="0 0 180 180" aria-hidden="true" focusable="false">
          <circle cx="90" cy="90" r="84" fill="#f5f8ef"/><circle cx="90" cy="90" r="67" fill="none" stroke="#bdd2a6" stroke-width="1.5" stroke-dasharray="2 5"/>
          <path d="M43 54C60 31 116 27 139 53M143 67c13 21 8 53-9 70M47 133c-17-17-21-44-10-65" fill="none" stroke="#87a7d7" stroke-width="1.5"/>
          <circle cx="43" cy="54" r="5" fill="#4e80c2"/><circle cx="139" cy="53" r="5" fill="#78a749"/><circle cx="134" cy="137" r="5" fill="#4e80c2"/><circle cx="37" cy="68" r="5" fill="#78a749"/>
          <circle cx="90" cy="90" r="48" fill="#fff" stroke="#dce7d3" stroke-width="2"/><circle cx="90" cy="90" r="39" fill="none" stroke="#e8eee2" stroke-width="5"/>
          <path d="M61 71a39 39 0 0 1 57-9" fill="none" stroke="#7fa64e" stroke-width="5" stroke-linecap="round"/><circle cx="90" cy="90" r="28" fill="#f8faf5"/>
          <text x="90" y="86" text-anchor="middle" fill="#365a34" font-size="9" font-weight="700" letter-spacing="1">SCORE</text><text x="90" y="101" text-anchor="middle" fill="#284b35" font-size="13" font-weight="800">MEALIM</text>
          <g fill="#315b19" font-size="8" font-weight="700" text-anchor="middle"><text x="42" y="42">IG</text><text x="139" y="41">FIBRES</text><text x="145" y="151">LIPIDES</text><text x="29" y="82">LÉGUMES</text></g>
          <path d="M81 119c4-8 13-8 18 0-4 9-13 11-18 0Zm7 0 7-7" fill="none" stroke="#78a749" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="mmm-mealim-invite-copy"><p class="mmm-mealim-invite-kicker">J’ANALYSE MA RECETTE</p><h2>J’analyse ma recette avec le score MEALIM</h2><p>Je découvre ses points forts nutritionnels et repère ce que je peux améliorer pour l’équilibrer davantage, si nécessaire. Pour recevoir des menus adaptés à mes besoins et à mon quotidien, je peux ensuite créer mon profil.</p></div>
      <div class="mmm-mealim-invite-actions"><router-link to="/atelier-mealim/recette">Analyser ma recette avec le score MEALIM <span aria-hidden="true">→</span></router-link><router-link to="/inscription">Créer mon profil gratuitement</router-link></div>
    </section>
    <section class="mmm-science-section">
      <b-container>
        <div class="mmm-science-intro">
          <div class="mmm-science-portrait-wrap">
            <img
              src="../assets/accueil/Photo Joffrey ZOLL 2026.png"
              alt="Dr Joffrey Zoll, physiologiste et spécialiste en nutrition et métabolisme"
              class="mmm-science-portrait"
              loading="lazy"
            />
            <div class="mmm-science-portrait-caption">
              <strong>Dr Joffrey Zoll</strong>
              <span>Physiologiste – Spécialiste en nutrition et métabolisme</span>
            </div>
          </div>
          <div class="mmm-science-heading">
            <p class="mmm-science-kicker">UNE DÉMARCHE SCIENTIFIQUE ET DURABLE</p>
            <h2>Une alimentation pensée pour ma santé… et pour demain</h2>
            <p><strong>L’algorithme de Mes Meilleurs Menus a été conçu avec le Dr Joffrey Zoll, physiologiste et chercheur en physiologie et métabolisme au CHU de Strasbourg</strong>, avec l’objectif de traduire les connaissances scientifiques en quelque chose de très concret : mieux manger au quotidien, sans renoncer au plaisir.</p>
            <p>Les principes nutritionnels de Mes Meilleurs Menus s'appuient sur les publications scientifiques, les méta-analyses et les grandes recommandations nutritionnelles officielles pour proposer une alimentation équilibrée, gourmande et plus durable.</p>
          </div>
        </div>

        <div class="mmm-science-algorithm">
          <img
            src="../assets/accueil/algorithme-scientifique-menus-personnalises.png"
            alt="Algorithme scientifique estimant les besoins nutritionnels à partir du profil pour personnaliser les menus"
            class="mmm-science-algorithm-img"
            loading="lazy"
          />
        </div>

        <div class="mmm-science-expert">
          <strong>Une démarche accompagnée par un Conseil scientifique</strong>
          <span>Mes Meilleurs Menus s'appuie sur un Conseil scientifique réunissant des spécialistes de la physiologie, de la nutrition et de la santé. Il accompagne le projet et apporte son expertise sur les principes nutritionnels et le développement des outils proposés aux utilisateurs.</span>
          <router-link to="/conseils-scientifique" class="mmm-science-expert-link">Découvrir notre Conseil scientifique →</router-link>
        </div>

        <h2 class="mmm-science-principles-title">Les grands principes nutritionnels qui guident nos menus</h2>

        <div class="mmm-science-landscape">
          <div class="mmm-science-principles mmm-science-principles-top">
            <article class="mmm-science-principle">
              <h3>D'inspiration méditerranéenne</h3>
              <p>Légumes, fruits, légumineuses, céréales, oléagineux, huiles végétales et produits de la mer occupent une place importante dans les menus.</p>
            </article>
            <article class="mmm-science-principle">
              <h3>Riches en fibres et avec des protéines adaptées</h3>
              <p>Les menus favorisent des aliments riches en fibres et veillent à des apports en protéines adaptés pour construire des repas complets et diversifiés.</p>
            </article>
            <article class="mmm-science-principle">
              <h3>Un meilleur profil glycémique</h3>
              <p>Je peux privilégier des repas ayant un impact plus modéré sur la glycémie et choisir des menus à faible profil glycémique.</p>
            </article>
          </div>
          <img
            src="../assets/accueil/alimentation-mediterraneenne-durable.png"
            alt="Alimentation méditerranéenne variée, équilibrée, locale et plus durable"
            class="mmm-science-landscape-img"
            loading="lazy"
          />
          <div class="mmm-science-principles mmm-science-principles-bottom">
            <article class="mmm-science-principle">
              <h3>Local et de saison autant que possible</h3>
              <p>Les recettes mettent en avant les fruits et légumes de saison et encouragent une cuisine fondée sur des produits disponibles localement.</p>
            </article>
            <article class="mmm-science-principle">
              <h3>Des repas complets et équilibrés</h3>
              <p>L'équilibre se construit sur la semaine, en veillant aux vitamines et principaux minéraux ainsi qu'à l'équilibre entre protéines, glucides, lipides et fibres.</p>
            </article>
            <article class="mmm-science-principle">
              <h3>Une alimentation plus respectueuse du climat</h3>
              <p>Les menus font davantage de place aux aliments végétaux et limitent la consommation de viande, en particulier de viande rouge, afin de contribuer à réduire l'impact environnemental de notre alimentation.</p>
            </article>
          </div>
        </div>



        <section class="patron-section py-5">
      <b-container>
        <div class="patron-card patron-card-visual">
          <div class="patron-food" role="img" aria-label="Assiette gastronomique de saison">
            <div class="patron-food-caption">Une cuisine<br>de saison<br>pour demain</div>
          </div>

          <div class="patron-info">
            <div class="patron-kicker">LE PARRAIN DE MES MEILLEURS MENUS</div>
            <h2 class="patron-name">Julien Schaffhauser</h2>
            <p class="patron-role">Chef du restaurant NYD – Table à vivre à 48° Nord</p>
            <div class="patron-divider"></div>

            <p class="patron-text patron-main-copy">
              <strong>Parrain de Mes Meilleurs Menus,</strong> il partage avec nous une même envie :
              proposer une cuisine gourmande, de saison et attentive à la qualité des produits.
            </p>

            <div class="patron-brands" aria-label="NYD et 48° Nord">
              <div class="patron-brand-block">
                <img class="patron-logo patron-logo-nyd"
                  src="../assets/accueil/Logo NYD(1).jpg"
                  alt="Logo NYD – Table à vivre" />
              </div>
              <div class="patron-brand-separator"></div>
              <div class="patron-brand-block">
                <img class="patron-logo patron-logo-48"
                  src="../assets/accueil/logo(2).jpg"
                  alt="Logo 48° Nord – Landscape Hôtel" />
              </div>
            </div>
          </div>

          <div class="michelin-badge patron-michelin">
            <div class="michelin-mark" aria-hidden="true">✤</div>
            <div>
              <strong>Étoile Verte MICHELIN 2026</strong>
              <span>Une distinction qui met en lumière l'engagement du restaurant pour une gastronomie plus durable.</span>
            </div>
          </div>

          <div class="patron-actions patron-actions-wide">
            <a class="patron-btn patron-btn-primary"
              href="https://hotel48nord.com/fr/deguster.html" target="_blank" rel="noopener noreferrer">
              Découvrir NYD <span aria-hidden="true">→</span>
            </a>
            <a class="patron-btn patron-btn-secondary"
              href="https://guide.michelin.com/fr/fr/grand-est/breitenbach_1538518/restaurant/nyd-48%C2%B0-nord"
              target="_blank" rel="noopener noreferrer">
              Voir dans le Guide MICHELIN <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </b-container>
    </section>

        <div class="mmm-science-cta">
          <h3>Mieux manger ne devrait pas être compliqué.</h3>
          <p>Mes Meilleurs Menus m'accompagne au quotidien : des menus personnalisés, des quantités adaptées, des recettes gourmandes et une liste de courses prête à l'emploi. Et c'est entièrement gratuit.</p>

          <div class="mmm-journey" aria-label="Le parcours Mes Meilleurs Menus">
            <div class="mmm-journey-labels" aria-hidden="true">
              <strong>Mon profil</strong>
              <strong>Ma semaine de menus</strong>
              <strong>Mes recettes</strong>
              <strong>Ma liste de courses</strong>
              <strong>À table !</strong>
            </div>
            <img class="mmm-journey-image" src="../assets/accueil/fonctionnement-mes-meilleurs-menus.png" alt="Parcours Mes Meilleurs Menus : personnalisation du profil, semaine de menus, recettes, liste de courses et repas" loading="lazy">
            <div class="mmm-journey-benefits" aria-hidden="true">
              <span>Bon pour ma santé</span>
              <span>Bon pour le climat</span>
              <span>Économique</span>
              <span>Gain de temps</span>
              <span>Pour toute la famille</span>
            </div>
          </div>

          <div class="mmm-autonomy">
            <h3>Des menus pour commencer. De meilleures habitudes pour continuer.</h3>
            <p><strong>Mes Meilleurs Menus ne cherche pas seulement à me dire quoi manger. Il m'aide progressivement à mieux comprendre et construire mon alimentation.</strong></p>
            <p>En utilisant des menus équilibrés, des portions adaptées et des recettes variées, j’apprends peu à peu à mieux composer mes repas, à diversifier mon alimentation et à faire des choix plus adaptés au quotidien.</p>
            <p><strong>L'objectif est que ces nouvelles habitudes deviennent naturelles et que je gagne progressivement en autonomie dans ma façon de m'alimenter.</strong></p>
            <p>Mais devenir autonome ne signifie pas être seul. <strong>Mes Meilleurs Menus reste à mes côtés pour m'accompagner dans la durée</strong>, grâce à différents outils — dont certains sont encore en développement — conçus pour m'aider à continuer à améliorer mon alimentation au quotidien.</p>
            <p>Le premier est déjà là : <strong>nos recettes de saison m'accompagnent toute l'année</strong> pour me donner de nouvelles idées de recettes gourmandes et favorables à ma santé. Je peux les rechercher selon mes envies, le temps dont je dispose ou mes choix alimentaires : végétariennes, sans gluten, à faible index glycémique, bon marché…</p>
            <p><strong>Apprendre à mieux manger, devenir plus autonome et continuer à être accompagné : c'est aussi ça, Mes Meilleurs Menus.</strong></p>
            <router-link class="mmm-autonomy-recipe" to="/recettes?season=actuelle">Trouver une recette de saison <span aria-hidden="true">→</span></router-link>
          </div>

          <div class="mmm-ready">
            <h3>Je commence ?</h3>
            <p>Je crée gratuitement mon profil et je découvre une semaine de menus adaptée à mes besoins, à mes goûts et à mon quotidien.</p>
          </div>
          <router-link class="mmm-science-main-btn" to="/inscription">Je crée gratuitement mon profil <span aria-hidden="true">→</span></router-link>
          <router-link class="mmm-science-link" to="/alimentation-positive-et-durable">Découvrir les bases scientifiques de notre démarche</router-link>
        </div>
      </b-container>
    </section>

    
    

    <!-- Program Description Section -->
    

    <section class="mmm-conclusion-section">
      <b-container>
        <div class="mmm-conclusion-grid">
          <div class="mmm-conclusion-visual">
            <img
              src="../assets/accueil/cuisiner-famille-produits-saison-mes-meilleurs-menus.png"
              alt="Cuisiner en famille avec des produits de saison pour une alimentation saine et durable"
              class="mmm-conclusion-image"
              loading="lazy"
            />
          </div>
          <div class="mmm-conclusion-copy">
            <p class="mmm-conclusion-kicker">À MOI DE JOUER !</p>
            <h2>Voilà où Mes Meilleurs Menus veut m’emmener</h2>
            <p class="mmm-conclusion-lead">
              Me redonner envie de cuisiner, en toute simplicité, avec des produits de saison achetés près de chez moi,
              pour me faire plaisir, à moi et à ma famille, tout en prenant soin de ma santé et du climat de la planète.
            </p>
            <p class="mmm-conclusion-signature">
              Des menus pour commencer. De meilleures habitudes pour continuer. L’autonomie comme objectif.
            </p>
            <router-link to="/inscription" class="mmm-conclusion-btn">
              Je m'inscris gratuitement <span aria-hidden="true">→</span>
            </router-link>
          </div>
        </div>
      </b-container>
    </section>


  </div>
</template>

<script>
export default {
  data() {
    return {
      programItems: [
        {
          imgSrc: require("../assets/accueil/MMMFichier-14x-8.png"),
          text: "Entièrement gratuits, car TOUT LE MONDE doit pouvoir améliorer son alimentation tout en protégeant le climat",
        },
        {
          imgSrc: require("../assets/accueil/MMMFichier-24x-8.png"),
          text: "De saison, savoureux avec des produits rigoureusement sélectionnés",
        },
        {
          imgSrc: require("../assets/accueil/MMMFichier-34x-8.png"),
          text: "Avec moins de viande et moins d'émissions de gaz à effet de serre",
        },
        {
          imgSrc: require("../assets/accueil/MMMFichier-44x-8.png"),
          text: "De type méditerranéen à faible indice glycémique conçu sur la base des derniers résultats scientifiques",
        },
        {
          imgSrc: require("../assets/accueil/MMMFichier-54x-8.png"),
          text: "Avec tous les apports nécessaires en 24 nutriments (fibres, vitamines et minéraux)",
        },
        {
          imgSrc: require("../assets/accueil/MMMFichier-74x-8-1.png"),
          text: "Personnalisés qui permettent des apports ajustés aux besoins nutritionnels et qui permettent une perte de poids",
        },
      ],
      images: [
        {
          src: require("@/assets/accueil/0766b27462876e809feab4ecc2299a30-4.jpeg"),
          alt: "Exemple de menu hebdomadaire personnalisé Mes Meilleurs Menus",
        },
        {
          src: require("@/assets/accueil/0766b27462876e809feab4ecc2299a30-3.jpeg"),
          alt: "Exemple de programme de repas équilibrés sur une semaine",
        },
        {
          src: require("@/assets/accueil/0766b27462876e809feab4ecc2299a30-2.jpeg"),
          alt: "Exemple de menus et recettes proposés pour la semaine",
        },
        {
          src: require("@/assets/accueil/0766b27462876e809feab4ecc2299a30-1.jpeg"),
          alt: "Exemple de planning hebdomadaire de repas Mes Meilleurs Menus",
        },
        {
          src: require("@/assets/accueil/0766b27462876e809feab4ecc2299a30-0.jpeg"),
          alt: "Exemple de semaine de menus équilibrés et personnalisés",
        },
      ],
    };
  },
};
</script>

<style scoped>
.mmm-hero {
  position: relative;
  overflow: hidden;
  padding: 112px 0 46px;
  background: #fbfff5;
}
.mmm-hero-container {
  width: min(96vw, 1840px);
  margin: 0 auto;
}
.mmm-hero-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 45%) minmax(0, 55%);
  gap: 18px;
  align-items: end;
}
.mmm-hero-copy {
  min-width: 0;
  max-width: 800px;
  padding: 0 0 34px;
}
.mmm-hero-brand {
  margin: 0 0 12px;
  color: #86b83e;
  font-family: "Brush Script MT", "Segoe Script", cursive;
  font-size: clamp(42px, 3.5vw, 58px);
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
}
.mmm-hero h1 {
  margin: 0 0 22px;
  color: #174f42;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(40px, 3vw, 57px);
  font-weight: 800;
  line-height: 1.07;
  letter-spacing: -.025em;
}
.mmm-hero-lead {
  max-width: 760px;
  margin: 0 0 26px;
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
  color: #3f5750;
  font-size: clamp(20px, 1.45vw, 24px);
  font-weight: 400;
  line-height: 1.48;
}
.mmm-hero-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 23px;
  border-radius: 10px;
  background: #8cbe32;
  color: #fff !important;
  font-size: 18px;
  font-weight: 800;
  text-decoration: none !important;
  box-shadow: 0 8px 20px rgba(92, 132, 32, .18);
}
.mmm-hero-button:hover { transform: translateY(-1px); }
.mmm-hero-benefits {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 720px;
  margin-top: 24px;
  color: #245749;
  font-size: 13px;
  text-align: center;
}
.mmm-benefit-icon {
  width: 54px;
  height: 54px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #f0f7e8;
  color: #245749;
  font-size: 25px;
  font-style: normal;
}
.mmm-hero-benefits span {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  line-height: 1.25;
}
.mmm-hero-visual {
  position: relative;
  min-width: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  overflow: hidden;
  background: #fbfff5;
}
.mmm-hero-image {
  position: relative;
  z-index: 1;
  width: 106%;
  max-width: 1040px;
  height: auto;
  display: block;
  margin: 0 -2% 0 0;
  -webkit-mask-image:
    linear-gradient(to right, transparent 0, rgba(0,0,0,.18) 2.5%, #000 8%),
    linear-gradient(to top, transparent 0, rgba(0,0,0,.28) 2.5%, #000 8%);
  -webkit-mask-composite: source-in;
  mask-image:
    linear-gradient(to right, transparent 0, rgba(0,0,0,.18) 2.5%, #000 8%),
    linear-gradient(to top, transparent 0, rgba(0,0,0,.28) 2.5%, #000 8%);
  mask-composite: intersect;
}
@media (max-width: 991px) {
  .mmm-hero-container { width: min(92vw, 780px); }
  .mmm-hero { padding: 58px 0 44px; }
  .mmm-hero-grid { grid-template-columns: 1fr; gap: 28px; text-align: center; }
  .mmm-hero-copy { max-width: 780px; margin: 0 auto; padding-bottom: 0; }
  .mmm-hero-lead { margin-left: auto; margin-right: auto; }
  .mmm-hero-benefits { grid-template-columns: repeat(2, minmax(120px, 1fr)); max-width: 520px; margin-left: auto; margin-right: auto; }
  .mmm-hero-visual { min-height: 0; justify-content: center; }
  .mmm-hero-image { width: 100%; max-width: 720px; margin-right: 0; }
}
@media (max-width: 575px) {
  .mmm-hero { padding: 40px 0 32px; }
  .mmm-hero h1 { font-size: 34px; }
  .mmm-hero-lead { font-size: 17px; }
  .mmm-hero-benefits { grid-template-columns: repeat(2, 1fr); gap: 18px 10px; }
  .mmm-hero-visual { min-height: 0; }
  .mmm-hero-image { width: 100%; }
}


.mmm-science-section {
  padding: 76px 0;
  background: #ffffff;
  color: #263b34;
}
.mmm-science-heading {
  max-width: 920px;
  margin: 0 auto 38px;
  text-align: center;
}
.mmm-science-kicker {
  margin-bottom: 8px;
  color: #85bb2f;
  font-weight: 800;
  letter-spacing: .08em;
}
.mmm-science-heading h2 {
  margin-bottom: 20px;
  color: #004c40;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(30px, 3vw, 43px);
  font-weight: 800;
}
.mmm-science-heading p {
  color: #4a5f58;
  font-size: 17px;
  line-height: 1.65;
}
.mmm-science-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
.mmm-science-card {
  padding: 27px 24px;
  border: 1px solid #dce9e5;
  border-radius: 17px;
  background: #f8fbfa;
}
.mmm-science-icon {
  margin-bottom: 13px;
  font-size: 30px;
}
.mmm-science-card h3 {
  margin-bottom: 10px;
  color: #004c40;
  font-size: 19px;
  font-weight: 800;
}
.mmm-science-card p {
  margin: 0;
  color: #52665f;
  line-height: 1.55;
}
.mmm-science-algorithm {
  max-width: 1180px;
  margin: 34px auto 54px;
  text-align: center;
}

.mmm-science-algorithm-img {
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto;
}

.mmm-science-landscape {
  position: relative;
  max-width: 1380px;
  min-height: 700px;
  margin: 28px auto 42px;
  padding: 0 18px;
}
.mmm-science-landscape-img {
  position: absolute;
  z-index: 0;
  left: 50%;
  top: 50%;
  width: 100%;
  max-width: 1120px;
  max-height: 560px;
  object-fit: contain;
  transform: translate(-50%, -48%);
  opacity: 0.98;
}
.mmm-science-principles {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 110px;
}
.mmm-science-principles-top {
  padding-top: 10px;
}
.mmm-science-principles-bottom {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 8px;
}
.mmm-science-principle {
  padding: 18px 22px;
  background: rgba(255,255,255,0.88);
  border-radius: 18px;
  backdrop-filter: blur(2px);
}
.mmm-science-principles-title {
  margin: 54px auto 28px;
  text-align: center;
  color: #285447;
  font-size: clamp(1.65rem, 2.4vw, 2.2rem);
  font-weight: 700;
}

.mmm-science-principle h3 {
  margin: 0 0 8px;
  color: #214f43;
  font-size: 1.18rem;
  font-weight: 800;
  line-height: 1.25;
}
.mmm-science-principle p {
  margin: 0;
  color: #4f625d;
  line-height: 1.55;
}
@media (max-width: 1100px) {
  .mmm-science-landscape { min-height: 760px; }
  .mmm-science-principles { column-gap: 38px; }
  .mmm-science-landscape-img { width: 94%; }
}
@media (max-width: 767px) {
  .mmm-science-landscape {
    min-height: 0;
    padding: 0;
  }
  .mmm-science-landscape-img {
    position: relative;
    left: auto;
    top: auto;
    width: 100%;
    max-height: none;
    margin: 14px auto 22px;
    transform: none;
  }
  .mmm-science-principles,
  .mmm-science-principles-bottom {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .mmm-science-principle {
    padding: 12px 6px;
    background: transparent;
    backdrop-filter: none;
  }
}
.mmm-science-expert {
  display: flex;
  flex-direction: column;
  max-width: 760px;
  margin: 34px auto 0;
  padding: 20px 26px;
  border-left: 5px solid #85bb2f;
  border-radius: 10px;
  background: #edf6f2;
  text-align: left;
}
.mmm-science-expert strong {
  color: #004c40;
  font-size: 18px;
}
.mmm-science-expert span {
  margin-top: 4px;
  color: #53675f;
}
.mmm-science-expert-link {
  display: inline-block;
  margin-top: 8px;
  color: #214f43;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.mmm-science-cta {
  max-width: 860px;
  margin: 42px auto 0;
  text-align: center;
}
.mmm-science-cta h3 {
  color: #004c40;
  font-size: 28px;
  font-weight: 800;
}
.mmm-science-cta p {
  margin: 12px 0 24px;
  color: #455b54;
  font-size: 17px;
  line-height: 1.6;
}
.mmm-journey {
  margin: 30px auto 34px;
}
.mmm-journey-image {
  display: block;
  width: 100%;
  height: auto;
}
.mmm-journey-labels,
.mmm-journey-benefits {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  align-items: center;
}
.mmm-journey-labels {
  margin-bottom: 5px;
  color: #004c40;
  font-size: 16px;
}
.mmm-journey-labels > :nth-child(1),
.mmm-journey-benefits > :nth-child(1) { transform: translateX(7px); }
.mmm-journey-labels > :nth-child(2),
.mmm-journey-benefits > :nth-child(2) { transform: translateX(4px); }
.mmm-journey-labels > :nth-child(4),
.mmm-journey-benefits > :nth-child(4) { transform: translateX(-4px); }
.mmm-journey-labels > :nth-child(5),
.mmm-journey-benefits > :nth-child(5) { transform: translateX(-7px); }
.mmm-journey-benefits {
  margin-top: 5px;
  color: #355c54;
  font-size: 14px;
  font-weight: 700;
}
.mmm-autonomy {
  max-width: 800px;
  margin: 38px auto 30px;
  padding: 26px 30px;
  border-radius: 16px;
  background: #f2f8f5;
}
.mmm-autonomy h3 {
  margin-bottom: 14px;
  font-size: 23px;
}
.mmm-autonomy p {
  margin: 10px 0;
  font-size: 16px;
}
.mmm-final-signup {
  margin: 32px auto 18px;
  text-align: center;
}
.mmm-final-signup h3 {
  margin-bottom: 8px;
  color: #004c40;
  font-size: 27px;
  font-weight: 800;
}
.mmm-final-signup p {
  max-width: 680px;
  margin: 0 auto;
  color: #455b54;
  font-size: 17px;
  line-height: 1.6;
}
.mmm-autonomy-recipe {
  display: inline-block;
  margin-top: 8px;
  color: #00695c;
  font-weight: 800;
  text-decoration: underline;
}
@media (max-width: 767px) {
  .mmm-journey {
    overflow-x: auto;
    padding-bottom: 6px;
  }
  .mmm-journey-image,
  .mmm-journey-labels,
  .mmm-journey-benefits {
    min-width: 760px;
  }
  .mmm-autonomy {
    padding: 22px 18px;
  }
}

.mmm-science-main-btn {
  display: inline-block;
  padding: 14px 25px;
  border-radius: 9px;
  background: #85bb2f;
  color: #fff;
  font-weight: 800;
  text-decoration: none;
}
.mmm-science-main-btn:hover {
  background: #004c40;
  color: #fff;
  text-decoration: none;
}
.mmm-science-link {
  display: block;
  margin-top: 17px;
  color: #00695c;
  font-weight: 700;
  text-decoration: underline;
}
@media (max-width: 991px) {
  .mmm-science-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 767px) {
  .mmm-science-section { padding: 54px 0; }
  .mmm-science-grid { grid-template-columns: 1fr; }
  .mmm-science-card { padding: 23px 20px; }
  .mmm-science-expert { padding: 18px 20px; }
  .mmm-science-main-btn { width: 100%; }
}

.mmm-recipe-search-section {
  padding: 12px 0 72px;
  background: #f7fbfa;
  color: #24352f;
}
.mmm-recipe-search-card {
  padding: 38px 42px;
  border-radius: 22px;
  background: #eaf5f1;
  border: 1px solid #d6e8e2;
}
.mmm-recipe-search-copy {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
}
.mmm-recipe-search-kicker {
  margin-bottom: 8px;
  color: #85bb2f;
  font-weight: 800;
  letter-spacing: 0.08em;
}
.mmm-recipe-search-copy h2 {
  margin-bottom: 14px;
  color: #004c40;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(28px, 2.6vw, 40px);
  font-weight: 800;
}
.mmm-recipe-search-copy > p:not(.mmm-recipe-search-kicker) {
  margin-bottom: 24px;
  color: #455b54;
  font-size: 17px;
  line-height: 1.65;
}
.mmm-recipe-search-btn {
  display: inline-block;
  padding: 13px 22px;
  border-radius: 9px;
  background: #85bb2f;
  color: #ffffff;
  font-weight: 700;
  text-decoration: none;
}
.mmm-recipe-search-btn:hover {
  background: #004c40;
  color: #ffffff;
  text-decoration: none;
}
@media (max-width: 767px) {
  .mmm-recipe-search-section { padding: 6px 0 52px; }
  .mmm-recipe-search-card { padding: 28px 20px; }
  .mmm-recipe-search-btn { width: 100%; }
}

.mmm-how-section {
  padding: 72px 0;
  background: #f7fbfa;
  color: #24352f;
}
.mmm-how-heading {
  max-width: 780px;
  margin: 0 auto 44px;
  text-align: center;
}
.mmm-how-kicker {
  margin-bottom: 8px;
  color: #85bb2f;
  font-weight: 800;
  letter-spacing: 0.08em;
}
.mmm-how-heading h2 {
  margin-bottom: 14px;
  color: #004c40;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(30px, 3vw, 44px);
  font-weight: 800;
}
.mmm-how-heading > p:last-child {
  color: #455b54;
  font-size: 18px;
  line-height: 1.6;
}
.mmm-how-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}
.mmm-how-step {
  position: relative;
  display: grid;
  grid-template-columns: 44% 56%;
  grid-template-rows: auto auto 1fr;
  min-height: 270px;
  padding: 22px 24px 22px 16px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #dce9e5;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(0, 76, 64, 0.06);
}
.mmm-how-top {
  display: contents;
}
.mmm-how-illustration {
  grid-column: 1;
  grid-row: 1 / 4;
  align-self: stretch;
  width: calc(100% - 14px);
  height: 100%;
  margin-right: 14px;
  object-fit: contain;
  object-position: center;
  border-radius: 12px;
}
.mmm-how-top .mmm-how-number {
  grid-column: 2;
  grid-row: 1;
  margin-bottom: 14px;
}
.mmm-how-number {
  display: flex;
  width: 42px;
  height: 42px;
  margin-bottom: 0;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #004c40;
  color: #ffffff;
  font-size: 20px;
  font-weight: 800;
}
 .mmm-how-step h3 {
  grid-column: 2;
  grid-row: 2;
  margin-bottom: 12px;
  color: #004c40;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 21px;
  font-weight: 800;
  line-height: 1.25;
}
 .mmm-how-step p {
  grid-column: 2;
  grid-row: 3;
  margin: 0;
  color: #455b54;
  font-size: 16px;
  line-height: 1.55;
}
.mmm-how-action {
  margin-top: 34px;
  text-align: center;
}
.mmm-how-btn {
  display: inline-block;
  padding: 13px 22px;
  border-radius: 9px;
  background: #004c40;
  color: #ffffff;
  font-weight: 700;
  text-decoration: none;
}
.mmm-how-btn:hover {
  background: #85bb2f;
  color: #ffffff;
  text-decoration: none;
}
@media (max-width: 991px) {
  .mmm-how-grid { grid-template-columns: 1fr; }
  .mmm-how-step { min-height: 260px; }
}
@media (max-width: 767px) {
  .mmm-how-section { padding: 52px 0; }
  .mmm-how-grid { grid-template-columns: 1fr; gap: 16px; }
  .mmm-how-heading { margin-bottom: 30px; }
  .mmm-how-step {
    grid-template-columns: 40% 60%;
    min-height: 245px;
    padding: 20px 18px 20px 12px;
  }
  .mmm-how-illustration {
    width: calc(100% - 10px);
    margin-right: 10px;
    object-fit: contain;
  }
  .mmm-how-btn { width: 100%; }
}

section {
  color: white;
}

/* Custom styles for carousel controls and indicators */
.custom-control-prev,
.custom-control-next {
  filter: invert(1);
  /* Invert colors to make the controls black */
}

.custom-control-icon {
  background-color: black;
  /* Ensure the background color of controls is black */
}

.carousel-indicators .indicator-button {
  background-color: black;
  /* Make indicators black */
}

.carousel-indicators .indicator-button.active {
  background-color: rgba(0, 0, 0, 0.75);
  /* Slightly transparent for active indicator */
}

.carousel {
  max-width: 800px;
}

.carousel-item img {
  height: 100%;

  object-fit: contain;
}

.amanise {
  font-size: 150%;
}

.cards {
  background-color: rgba(133, 187, 47, 0.28);
  border: none;
  border-radius: 25px;
}

.card-title {
  color: #ffffff;
  font-family: "Amanise", Sans-serif;
  font-size: 50px;
  font-weight: 500;
}

.img-fluids {
  width: 100%;
  height: 125px;
  height: auto;
  max-width: 125px;
  max-height: 120px;
}

.banner {
  background: linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
    url("../assets/accueil/repas-dehors.png") center center fixed no-repeat;
  background-size: cover;
  height: 700px;
  font-size: larger;
  padding-top: 0;

  /* height: 700px; */
  /* background-attachment: fixed; */
}

.banner-descr {
  font-size: 300%;
  font-weight: 400;
  color: white;
}

.banner-2 {
  background: url("../assets/meilleure-menus.png") center center fixed no-repeat;
  background-size: cover;

  color: black;
}

/* Media query for phone-sized screens */
@media only screen and (max-width: 991px) {
  .banner-3 {
    padding-bottom: 250px;
  }
}

.btn-order {
  color: white;
  background-color: #004c40;
  padding: 5px 10px 5px 10px;
  transition: 0.3s;
  font-family: "TangoSans";
}

.btn-order:hover {
  color: #004c40;
  background-color: white;
  border-color: #004c40;
}

.btn-order2 {
  color: white;
  background-color: rgb(133, 187, 47);
  transition: 0.3s;
  font-family: "TangoSans";
  border: none;
}

.btn-order2:hover {
  color: white;
  background-color: #004c40;
  border-color: #004c40;
}

/*  SVG container */
.elementor-shape-fill-1 {
  fill: #fff;
}

.elementor-shape-fill-2 {
  fill: rgba(133, 187, 47, 0.28);
}

.elementor-shape-fill-3 {
  fill: #fff;
}

/* svg {
    height: 70px;
    width: 100%
} */

.svg-container {
  height: 70px;
  width: 100%;
}

/* A découvrir section */
.image-wrapper {
  position: relative;
  height: 510px;
  overflow: hidden;
  transition: 0.3s;
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: box-shadow 0.3s ease;
}

.image-wrapper:hover img {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  transition: 0.5s;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: 0.3s;
}

.overlay:hover {
  background-image: linear-gradient(rgba(133, 187, 47, 0.28) 0%,
      rgb(0, 0, 0) 99%);
}

.overlay-text {
  color: white;
  font-size: 30px;
  font-family: "Tangosans";
}

.text-overlay {
  color: white;
  text-decoration: none;
}

.funding-section {
  padding: 10px 8px 20px;
  background: #ffffff;
}

.funding-wrapper {
  padding-left: 0;
  padding-right: 0;
}

.funding-box {
  background: #005b4f;
  border: 4px solid #ffffff;
  border-radius: 28px;
  padding: 22px 40px 26px;
  color: #ffffff;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.funding-title {
  margin: 0 0 18px;
  text-align: center;
  color: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 800;
  line-height: 1.15;
  font-size: clamp(28px, 2.2vw, 42px);
  letter-spacing: -0.3px;
}

.funding-content {
  max-width: 100%;
}

.funding-line {
  margin: 0 0 8px;
  color: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(18px, 1.15vw, 29px);
  font-weight: 700;
  line-height: 1.25;
}

.funding-paragraph {
  margin: 0 0 16px;
  color: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(18px, 1.1vw, 26px);
  font-weight: 700;
  line-height: 1.35;
}

.funding-paragraph:last-child {
  margin-bottom: 0;
}

.funding-links a {
  color: #9bd235;
  overflow-wrap: anywhere;
}

.funding-links a:hover {
  color: #ffffff;
}

.funding-highlight {
  display: inline-block;
  margin-right: 8px;
  color: #9bd235;
  font-weight: 900;
}

.funding-emoji {
  display: inline-block;
  margin-right: 8px;
}

.funding-deadline {
  margin: 4px 0 18px;
  color: #9bd235;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(18px, 1.1vw, 27px);
  font-weight: 900;
  line-height: 1.2;
}

.funding-btn {
  display: block;
  width: fit-content;
  min-width: 180px;
  margin: 6px auto 0;
  padding: 12px 36px;
  background: #95c83d;
  color: #ffffff;
  text-decoration: none;
  text-align: center;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(18px, 1.2vw, 30px);
  font-weight: 800;
  line-height: 1;
  border-radius: 6px;
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.08);
  transition: 0.2s ease;
}

.funding-btn:hover {
  background: #89bc31;
  color: #ffffff;
  text-decoration: none;
}

@media (max-width: 991px) {
  .funding-box {
    border-radius: 24px;
    padding: 20px 16px 24px;
  }

  .funding-title {
    margin-bottom: 16px;
    line-height: 1.18;
  }

  .funding-line,
  .funding-deadline {
    line-height: 1.3;
  }

  .funding-btn {
    width: 100%;
    max-width: 280px;
  }
}

@media (max-width: 576px) {
  .funding-section {
    padding: 8px 6px 16px;
  }

  .funding-box {
    border-width: 3px;
    border-radius: 22px;
    padding: 18px 14px 20px;
  }

  .funding-title {
    font-size: 24px;
  }

  .funding-line {
    font-size: 18px;
  }

  .funding-deadline {
    font-size: 18px;
  }

  .funding-btn {
    padding: 12px 20px;
    font-size: 18px;
  }
}

.patron-section {
  background: #f5f8f2;
}

.patron-card {
  max-width: 980px;
  margin: 0 auto;
  padding: 42px 48px;
  background: #ffffff;
  border-radius: 24px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 76, 64, 0.08);
  border: 1px solid rgba(0, 76, 64, 0.08);
}

.patron-kicker {
  color: #6f9f2f;
  font-weight: 800;
  letter-spacing: 0.12em;
  font-size: 1rem;
  margin-bottom: 8px;
}

.patron-name {
  color: #004c40;
  font-size: 2.25rem;
  font-weight: 800;
  margin-bottom: 4px;
}

.patron-role {
  font-size: 1.15rem;
  margin-bottom: 24px;
  color: #3f4a46;
}

.patron-lead,
.patron-text {
  color: #263b2c;
  font-size: 1.16rem;
  line-height: 1.55;
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
}

.patron-brands {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 42px;
  flex-wrap: wrap;
  margin: 28px 0 22px;
}

.patron-brand {
  color: #222;
  font-weight: 800;
}

.patron-brand-nyd {
  font-size: 2.2rem;
  letter-spacing: 0.08em;
}

.patron-brand-48 {
  font-size: 1.75rem;
  letter-spacing: 0.04em;
}

.michelin-badge {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 720px;
  margin: 0 auto 26px;
  padding: 14px 18px;
  border-radius: 14px;
  background: #eef5e7;
  color: #263b2c;
  font-size: 1.05rem;
}

.michelin-badge strong {
  font-size: 1.2rem;
  color: #004c40;
}

.patron-actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
}

.patron-btn {
  display: inline-block;
  padding: 12px 22px;
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none !important;
  transition: transform 0.15s ease;
}

.patron-btn:hover {
  transform: translateY(-1px);
}

.patron-btn-primary {
  background: #004c40;
  color: #ffffff !important;
}

.patron-btn-secondary {
  background: #ffffff;
  color: #004c40 !important;
  border: 2px solid #004c40;
}

@media (max-width: 576px) {
  .patron-section {
    padding-top: 28px !important;
    padding-bottom: 28px !important;
  }

  .patron-card {
    padding: 28px 20px;
    border-radius: 18px;
  }

  .patron-name {
    font-size: 1.85rem;
  }

  .patron-role,
  .patron-lead,
  .patron-text {
    font-size: 1.08rem;
  }

  .patron-brands {
    gap: 24px;
    margin: 22px 0 18px;
  }

  .patron-actions {
    flex-direction: column;
  }

  .patron-btn {
    width: 100%;
  }
}


/* Parrain - composition inspirée de la maquette validée */
.patron-card-visual {
  display: grid;
  grid-template-columns: minmax(280px, 42%) 1fr;
  overflow: hidden;
  padding: 0;
  text-align: center;
}

.patron-food {
  min-height: 470px;
  position: relative;
  background:
    linear-gradient(90deg, rgba(20, 55, 35, .08), rgba(255,255,255,.08)),
    url("../assets/accueil/plat-gastronomique-parrain-julien-schaffhauser.png") center/cover no-repeat;
}

.patron-food-caption {
  position: absolute;
  top: 34px;
  left: 34px;
  color: white;
  font-family: 'Amanise', cursive;
  font-size: 2.5rem;
  line-height: .92;
  text-align: left;
  transform: rotate(-5deg);
  text-shadow: 0 2px 12px rgba(0,0,0,.28);
}

.patron-info {
  padding: 42px 46px 22px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.patron-divider {
  width: 68px;
  height: 4px;
  margin: 4px auto 24px;
  border-radius: 4px;
  background: #85bb2f;
}

.patron-main-copy {
  max-width: 650px;
  margin-bottom: 24px;
}

.patron-brand-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 150px;
}

.patron-brand-block small {
  margin-top: 2px;
  font-size: .65rem;
  letter-spacing: .22em;
  color: #4d5552;
}

.patron-brand-separator {
  width: 1px;
  height: 72px;
  background: #cbd3ce;
}

.patron-michelin {
  grid-column: 1 / -1;
  flex-direction: row;
  align-items: center;
  text-align: left;
  gap: 20px;
  margin: 0 7% 24px;
  padding: 18px 28px;
}

.michelin-mark {
  flex: 0 0 auto;
  font-size: 3.4rem;
  line-height: 1;
  color: #23662f;
}

.patron-michelin span {
  display: block;
}

.patron-actions-wide {
  grid-column: 1 / -1;
  padding: 0 7% 38px;
}

.patron-actions-wide .patron-btn {
  min-width: 260px;
}

@media (max-width: 767px) {
  .patron-card-visual {
    display: block;
  }

  .patron-food {
    min-height: 280px;
  }

  .patron-food-caption {
    top: 24px;
    left: 24px;
    font-size: 2rem;
  }

  .patron-info {
    padding: 28px 20px 18px;
  }

  .patron-michelin {
    margin: 0 20px 22px;
    padding: 16px;
  }

  .michelin-mark {
    font-size: 2.7rem;
  }

  .patron-actions-wide {
    padding: 0 20px 28px;
  }

  .patron-actions-wide .patron-btn {
    min-width: 0;
  }
}

.patron-logo {
  display: block;
  max-width: 210px;
  max-height: 90px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.patron-logo-48 {
  max-width: 230px;
}

@media (max-width: 575px) {
  .patron-logo {
    max-width: 135px;
    max-height: 70px;
  }

  .patron-logo-48 {
    max-width: 145px;
  }
}

/* Présentation concrète de Mes Meilleurs Menus */
.mmm-features-section {
  padding: 58px 0 42px;
  background: #fff;
}

.mmm-features-heading {
  max-width: 820px;
  margin: 0 auto 34px;
  text-align: center;
}

.mmm-features-kicker {
  margin-bottom: 8px;
  color: #5f8f2d;
  font-size: .82rem;
  font-weight: 800;
  letter-spacing: .12em;
}

.mmm-features-heading h2 {
  margin-bottom: 10px;
  color: #004c40;
  font-family: Arial, sans-serif;
  font-size: clamp(1.8rem, 3vw, 2.55rem);
  font-weight: 800;
}

.mmm-features-heading > p:last-child {
  color: #46524d;
  font-size: 1.08rem;
}

.mmm-feature-card {
  height: 100%;
  padding: 28px 25px;
  border: 1px solid #e3eadf;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(24, 62, 43, .07);
  text-align: left;
}


.mmm-feature-illustration {
  width: 100%;
  height: 185px;
  margin-bottom: 20px;
  border-radius: 14px;
  object-fit: contain;
  background: #fff;
}

.mmm-feature-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 18px;
  border-radius: 50%;
  background: rgba(133, 187, 47, .18);
  color: #004c40;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  font-weight: 800;
}

.mmm-feature-card h3 {
  margin-bottom: 12px;
  text-align: center;
  color: #004c40;
  font-family: Arial, sans-serif;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.25;
}

.mmm-feature-card p {
  margin: 0;
  color: #36433e;
  font-size: 1rem;
  line-height: 1.6;
}

@media (max-width: 767px) {
  .mmm-features-section {
    padding: 42px 0 28px;
  }

  .mmm-features-heading {
    margin-bottom: 24px;
  }

  .mmm-feature-card {
    padding: 23px 20px;
  }
}


/* Comment ça marche : mise en page compacte à 100 % */
.mmm-how-step {
  display: block;
  min-height: 0;
  padding: 22px 26px 26px;
  overflow: visible;
}
.mmm-how-top {
  display: flex;
  min-height: 96px;
  margin-bottom: 14px;
  align-items: center;
  justify-content: flex-start;
  gap: 22px;
}
.mmm-how-top .mmm-how-number {
  flex: 0 0 42px;
  margin: 0;
}
.mmm-how-illustration {
  display: block;
  width: 150px;
  height: 96px;
  margin: 0;
  object-fit: contain;
  object-position: center;
  border-radius: 12px;
}
.mmm-how-step h3 {
  margin: 0 0 12px;
  font-size: 21px;
  line-height: 1.25;
}
.mmm-how-step p {
  margin: 0;
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
}
@media (max-width: 767px) {
  .mmm-how-step {
    min-height: 0;
    padding: 20px;
  }
  .mmm-how-top {
    min-height: 82px;
    gap: 16px;
  }
  .mmm-how-illustration {
    width: 125px;
    height: 82px;
  }
  .mmm-how-step p {
    text-align: left;
  }
}


/* Affichage desktop optimisé à 100 % : évite le recouvrement par l'en-tête fixe */
@media (min-width: 992px) {
  .mmm-hero {
    padding-top: 205px;
  }

  .mmm-science-section {
    padding-top: 105px;
  }

  .mmm-science-landscape {
    max-width: 1240px;
    min-height: 650px;
  }

  .mmm-science-landscape-img {
    max-width: 1040px;
    max-height: 520px;
  }

  .mmm-science-principles {
    column-gap: 72px;
  }

  .mmm-science-principle {
    padding: 16px 18px;
  }

  .mmm-science-principle h3 {
    font-size: 1.08rem;
  }

  .mmm-science-principle p {
    font-size: .95rem;
    line-height: 1.5;
  }
}


.mmm-conclusion-section {
  padding: 92px 0 78px;
  background: #f7faef;
}
.mmm-conclusion-grid {
  display: grid;
  grid-template-columns: minmax(0, 56%) minmax(0, 44%);
  gap: 0;
  align-items: stretch;
  max-width: 1320px;
  margin: 0 auto;
}
.mmm-conclusion-visual {
  min-width: 0;
  align-self: stretch;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  background: #f7faef;
}
.mmm-conclusion-image {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 500px;
  object-fit: cover;
  object-position: 62% center;
}
.mmm-conclusion-copy {
  max-width: none;
  padding: 48px 56px 44px 42px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #f7faef;
}
.mmm-conclusion-kicker {
  margin: 0 0 10px;
  color: #85bb2f;
  font-weight: 800;
  letter-spacing: .08em;
}
.mmm-conclusion-copy h2 {
  margin: 0 0 22px;
  text-align: center;
  color: #004c40;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(32px, 2.6vw, 44px);
  font-weight: 800;
  line-height: 1.12;
}
.mmm-conclusion-lead {
  margin: 0 0 20px;
  color: #455b54;
  font-size: 18px;
  line-height: 1.6;
}
.mmm-conclusion-signature {
  margin: 0 0 28px;
  color: #214f43;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.5;
}
.mmm-conclusion-btn {
  display: inline-block;
  padding: 14px 24px;
  border-radius: 9px;
  background: #85bb2f;
  color: #fff !important;
  font-size: 17px;
  font-weight: 800;
  text-decoration: none !important;
}
.mmm-conclusion-btn:hover {
  background: #004c40;
}
@media (max-width: 767px) {
  .mmm-conclusion-section {
    padding: 50px 0;
  }
  .mmm-conclusion-grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .mmm-conclusion-visual {
    order: 2;
  }
  .mmm-conclusion-image {
    height: auto;
    min-height: 0;
    object-fit: contain;
  }
  .mmm-conclusion-copy {
    order: 1;
    padding: 0 8px;
    text-align: center;
  }
  .mmm-conclusion-lead {
    font-size: 17px;
  }
  .mmm-conclusion-btn {
    width: 100%;
  }
}


/* Principes scientifiques : titres centrés et textes justifiés */
.mmm-science-principle h3 {
  text-align: center;
}
.mmm-science-principle p {
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
}

/* Harmonisation typographique accueil */
.mmm-features-section p,
.mmm-how-section p,
.mmm-recipe-search-section p,
.mmm-science-section p,
.patron-section p,
.mmm-conclusion-section p {
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
}

/* Les éléments volontairement centrés restent centrés */
.mmm-features-heading p,
.mmm-how-heading p,
.mmm-science-heading p,
.mmm-recipe-search-section .mmm-recipe-search-copy p,
.mmm-conclusion-kicker {
  text-align: center;
}

/* Comment ça marche : version desktop plus compacte à 100 % */
@media (min-width: 992px) {
  .mmm-how-grid { gap: 22px; }
  .mmm-how-step {
    min-height: 0;
    padding: 22px 26px 24px;
  }
  .mmm-how-top {
    min-height: 82px;
    margin-bottom: 4px;
    align-items: center;
  }
  .mmm-how-illustration {
    width: 132px;
    height: 88px;
    margin: -2px 4px 0 auto;
  }
  .mmm-how-step h3 {
    margin-bottom: 10px;
    font-size: 20px;
    line-height: 1.22;
  }
  .mmm-how-step p {
    font-size: 16px;
    line-height: 1.48;
  }
}

/* Comment ça marche : titre centré, numéro à gauche, illustration agrandie à droite */
.mmm-how-step {
  position: relative;
  padding-top: 30px;
}
.mmm-how-top {
  position: relative;
  display: flex;
  min-height: 112px;
  margin-bottom: 12px;
  align-items: flex-start;
  justify-content: space-between;
}
.mmm-how-top .mmm-how-number {
  flex: 0 0 42px;
  margin: 0;
}
.mmm-how-illustration {
  width: 170px;
  height: 112px;
  margin: -8px 0 0 auto;
  object-fit: contain;
  object-position: center;
}
.mmm-how-step h3 {
  margin: 0 0 14px;
  text-align: center;
}
.mmm-how-step p {
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
}

@media (max-width: 767px) {
  .mmm-features-section p,
  .mmm-how-section p,
  .mmm-recipe-search-section p,
  .mmm-science-section p,
  .patron-section p,
  .mmm-conclusion-section p {
    text-align: left;
  }
  .mmm-how-top {
    min-height: 92px;
  }
  .mmm-how-illustration {
    width: 140px;
    height: 92px;
  }
  .mmm-how-step h3 {
    text-align: center;
  }
}


/* Portrait Joffrey - introduction scientifique */
.mmm-science-intro {
  display: grid;
  grid-template-columns: 315px minmax(0, 1fr);
  gap: 46px;
  align-items: center;
  max-width: 1080px;
  margin: 0 auto 36px;
}

.mmm-science-portrait-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mmm-science-portrait {
  display: block;
  width: 100%;
  max-width: 295px;
  height: 365px;
  object-fit: cover;
  object-position: center top;
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(0, 76, 64, .12);
}

.mmm-science-portrait-caption {
  margin-top: 14px;
  text-align: center;
}

.mmm-science-portrait-caption strong {
  display: block;
  color: #28584c;
  font-size: 1.15rem;
  margin-bottom: 3px;
}

.mmm-science-portrait-caption span {
  display: block;
  font-size: .86rem;
  line-height: 1.35;
  white-space: nowrap;
}

.mmm-science-intro .mmm-science-heading {
  max-width: none;
  margin: 0;
}

@media (max-width: 767px) {
  .mmm-science-intro {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: 30px;
  }

  .mmm-science-portrait {
    max-width: 240px;
    height: 300px;
  }

  .mmm-science-portrait-caption span {
    white-space: normal;
  }
}


.mmm-mealim-invite{max-width:1120px;margin:36px auto 54px;padding:30px 34px;background:#f4f7ed;border:1px solid #dfe8d6;border-radius:18px;display:flex;align-items:center;justify-content:space-between;gap:28px}.mmm-mealim-invite-copy{max-width:680px}.mmm-mealim-invite-kicker{margin:0 0 8px;color:#617c45;font-size:12px;font-weight:800;letter-spacing:.08em}.mmm-mealim-invite h2{margin:0 0 8px;color:#294c24;font-size:clamp(22px,3vw,32px)}.mmm-mealim-invite-copy>p:last-child{margin:0;color:#536151;line-height:1.6}.mmm-mealim-invite-actions{display:flex;flex-direction:column;align-items:flex-start;gap:12px;flex-shrink:0}.mmm-mealim-invite-actions a:first-child{padding:12px 18px;border-radius:24px;background:#315b19;color:#fff;font-weight:700;text-decoration:none}.mmm-mealim-invite-actions a:last-child{color:#315b19;text-decoration:underline}@media(max-width:700px){.mmm-mealim-invite{margin:24px 16px 36px;padding:22px;display:block}.mmm-mealim-invite-actions{margin-top:18px}}
.mmm-mealim-invite{max-width:1320px;margin:36px auto 54px;padding:28px 34px;background:#f4f7ed;border:1px solid #dfe8d6;border-radius:18px;display:grid;grid-template-columns:150px minmax(0,1fr) auto;align-items:center;gap:28px}.mmm-mealim-invite-visual{width:150px;height:150px;display:grid;place-items:center}.mmm-mealim-invite-visual svg{display:block;width:100%;height:100%}.mmm-mealim-invite-copy{max-width:760px}.mmm-mealim-invite-actions{max-width:310px}.mmm-mealim-invite-actions a:first-child{line-height:1.4}@media(max-width:900px){.mmm-mealim-invite{grid-template-columns:112px minmax(0,1fr);gap:20px;padding:24px}.mmm-mealim-invite-visual{width:112px;height:112px}.mmm-mealim-invite-actions{grid-column:2;flex-direction:row;flex-wrap:wrap;align-items:center;max-width:none}}@media(max-width:700px){.mmm-mealim-invite{margin:24px 16px 36px;padding:22px;grid-template-columns:76px minmax(0,1fr);gap:14px}.mmm-mealim-invite-visual{width:76px;height:76px}.mmm-mealim-invite h2{font-size:22px}.mmm-mealim-invite-actions{grid-column:1 / -1;display:flex;flex-direction:column;align-items:stretch;margin-top:4px}.mmm-mealim-invite-actions a{text-align:center}}
</style>
